import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import p from '../../../package.json';
import Spinner from '../../components/spinner/Spinner.component';
import { GameContext } from '../../core/context/game.context';
import { configDataLayer, pushGtmDataLayer } from '../../core/helper/gtm.helper';
import { safeImageImport } from '../../core/helper/image.helper';
import { randomHelper } from '../../core/helper/random.helper';
import { IPreset, PresetId } from '../../core/interfaces/instaces.interfaces';
import './tutorial.style.scss';

export default function Tutorial() {
  const navigate = useNavigate();
  const gameCtx = React.useContext(GameContext);
  const theme = gameCtx.getTheme();
  const instance = gameCtx.getInstance();
  const [styleState, setStyleState] = useState({ section: null });
  const presets = gameCtx.getPreset() ? [gameCtx.getPreset()] : gameCtx.getInstance()?.presets;
  const mediaQuery = window.matchMedia('(max-width: 767.98px)');
  const { i18n } = useTranslation();

  useEffect(() => {
    gameCtx.deleteGame();
  }, [instance]);

  useEffect(() => {
    if (instance) {
      const manageBg = (arg: MediaQueryListEvent, rndValue: number) => {
        if (!instance) {
          return;
        }
        const size = arg.matches ? 'small' : 'big';

        setStyleState((state) => ({
          ...state,
          section: { backgroundImage: `url(${instance.presets[0]?.background || safeImageImport(`assets/images/${instance.id}/bg/${size}/0${rndValue}`)})` },
        }));
      };

      const rndValue = randomHelper(instance.configurations.bgQuantity);
      const changeHandler = (e: MediaQueryListEvent) => manageBg(e, rndValue);

      manageBg({ matches: mediaQuery.matches } as MediaQueryListEvent, rndValue);
      mediaQuery.addEventListener('change', changeHandler);
      return () => mediaQuery.removeEventListener('change', changeHandler);
    }
  }, [instance]);

  function start(presetId: PresetId) {
    const preset = gameCtx.getInstance().presets.find((p) => p.id === presetId);
    gameCtx.setScore(0);
    configDataLayer(i18n.language, gameCtx.getInstance().name, preset.name);
    pushGtmDataLayer('gameStart');
    navigate(
      `/${gameCtx.getLanguage()}/${gameCtx.getInstance().id}/${gameCtx.getInstance().name}/${preset.id}/${preset.name}/game`
    );
  }

  return (
    <section className="TutorialComponent" style={styleState.section}>
      {!instance ? (
        <Spinner primaryColor="#7DDC55" secondaryColor="#FE6E5C" />
      ) : (
        <div className={`container ${presets.length === 1 ? 'container--lonely' : ''}`}>
          <div className="container__card container__card--big">
            <img
              className="container__logo"
              src={instance && (instance.presets[0]?.logo || safeImageImport(`assets/images/${instance.id}/loghi/logo-inline`))}
              alt="loquis-challenge-logo"
              width="220"
            />
            <hr className="divisor" />
            <h3 className="container__title" style={{ marginBottom: '20px', color: theme.main[0] }}>
              {instance.presets[0]?.title || instance.labelsSet?.tutorial?.title}
            </h3>
            <p className="container__card__text" style={{ marginBottom: '20px' }}>
              {instance.presets[0]?.subtitle || instance.labelsSet?.tutorial?.subtitle}
            </p>
            <p
              className="container__card__text"
              style={{ marginBottom: '20px' }}
              dangerouslySetInnerHTML={{ __html: (instance.presets[0]?.tagline || instance.labelsSet?.tutorial?.tagline || '').toString() }}
            ></p>
          </div>
          {presets.map((preset: IPreset, idx: number) => (
            <div
              key={idx}
              className={
                'container__card container__card--little' + (presets.length === 1 ? ' container__card--lonely' : '')
              }
            >
              <p className="container__card__text">
                <strong>{preset.maxQuestions}</strong> {i18n.t('tutorial.questions')}
              </p>
              <p className="container__card__text">
                <strong>{preset.options}</strong> {i18n.t('tutorial.ans-opt')}
              </p>
              <p className="container__card__text">
                <strong>{preset.userResponseTO}</strong> {i18n.t('tutorial.time')}
              </p>
              <p className="container__card__text">
                <strong>{preset.maxWrongQuestions}</strong> {i18n.t('tutorial.errors-opt')}
              </p>
              <button
                className={'basicButton' + (idx === 1 ? ' hard' : '')}
                style={{ color: theme.main[0], backgroundColor: theme.main[2] }}
                onClick={start.bind(null, preset.id)}
              >
                {i18n.t('tutorial.play')}{' '}
              </button>
            </div>
          ))}
          <div className="container__footer">v{p.version}</div>
        </div>
      )}
    </section>
  );
}
